if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this._limitOneSubmitting = false;
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (this.dataset.limitOnePurchase !== 'true' || !event.cartData) return;
          this.syncLimitOneFromCart(event.cartData);
        });
      }

      disconnectedCallback() {
        this.cartUpdateUnsubscriber?.();
      }

      getLimitOnePurchaseError() {
        if (this.dataset.limitOnePurchase !== 'true') return null;

        const strings = window.limitOnePurchaseStrings || {};
        if (this.dataset.limitOnePurchased === 'true') {
          return strings.alreadyPurchased;
        }

        const quantity = parseInt(this.form.querySelector('[name=quantity]')?.value || '1', 10);
        if (quantity > 1) {
          return strings.maxQuantity;
        }

        const cartQty = parseInt(this.dataset.limitOneCartQty || '0', 10);
        if (cartQty >= 1) {
          return strings.inCart;
        }

        return null;
      }

      getProductCartQuantity(cartData) {
        if (!cartData?.items?.length) return 0;

        const productId = parseInt(this.dataset.productId, 10);
        const variantId = parseInt(this.variantIdInput?.value, 10);
        let qty = 0;

        for (const item of cartData.items) {
          if (productId && item.product_id === productId) {
            qty += item.quantity;
          } else if (variantId && item.variant_id === variantId) {
            qty += item.quantity;
          }
        }

        return qty;
      }

      syncLimitOneFromCart(cartData) {
        if (this.dataset.limitOnePurchase !== 'true') return;

        this.dataset.limitOneCartQty = String(this.getProductCartQuantity(cartData));
        this.applyLimitOnePurchaseUI();
      }

      applyLimitOnePurchaseUI() {
        if (this.dataset.limitOnePurchase !== 'true') return;

        const strings = window.limitOnePurchaseStrings || {};
        const alreadyPurchased = this.dataset.limitOnePurchased === 'true';
        const cartQty = parseInt(this.dataset.limitOneCartQty || '0', 10);
        const blocked = alreadyPurchased || cartQty >= 1;

        if (blocked) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
          if (this.variantIdInput) this.variantIdInput.disabled = true;
          if (this.submitButtonText) {
            this.submitButtonText.textContent = alreadyPurchased
              ? strings.alreadyPurchased
              : strings.inCart;
          }
          return;
        }

        this.submitButton.removeAttribute('disabled');
        this.submitButton.removeAttribute('aria-disabled');
        if (this.variantIdInput) this.variantIdInput.disabled = false;
        if (this.submitButtonText && window.variantStrings?.addToCart) {
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if (this.submitButton.hasAttribute('disabled')) return;
        if (this._limitOneSubmitting) return;

        const limitOneError = this.getLimitOnePurchaseError();
        if (limitOneError) {
          this.handleErrorMessage(limitOneError);
          return;
        }

        this.handleErrorMessage();

        this._limitOneSubmitting = true;
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }

            if (this.dataset.limitOnePurchase === 'true') {
              this.syncLimitOneFromCart(response);
              if (parseInt(this.dataset.limitOneCartQty || '0', 10) < 1) {
                this.dataset.limitOneCartQty = '1';
              }
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            this._limitOneSubmitting = false;
            this.querySelector('.loading__spinner').classList.add('hidden');

            if (!this.error) {
              if (this.dataset.limitOnePurchase === 'true') {
                this.applyLimitOnePurchaseUI();
              } else {
                this.submitButton.removeAttribute('aria-disabled');
              }
            }

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
